import { distance as getDistance } from "@turf/distance"
import { point } from "@turf/helpers"
import { Loader } from "components/LEGACY/styled-components/Icons"
import Input from "components/utils/InputField"
import { useFilteredShops } from "hooks/algolia/useFilteredShops"
import useDebounce from "hooks/misc/useDebounce"
import useInView from "hooks/misc/useInVIew"
import { useUserLocation } from "hooks/misc/useUserLocation"
import "leaflet/dist/leaflet.css"
import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"
import { useEffect, useMemo, useState } from "react"
import { Cash, GooglePlay } from "styled-icons/bootstrap"
import { Paypal } from "styled-icons/boxicons-logos"
import { CreditCard } from "styled-icons/boxicons-regular"
import { Business } from "styled-icons/boxicons-solid"
import { AppStore } from "styled-icons/fluentui-system-filled"
import { Location } from "styled-icons/ionicons-sharp"
import { ShopAlgolia } from "types/algolia/shopAlgolia"
import { DistributionOption } from "types/firestore/distributionOption"

const ShopMap = dynamic(() => import("components/discovery/ShopMap"), {
  ssr: false,
})

export default function DiscoveryPage() {
  const router = useRouter()
  const { q: queryParam } = router.query
  const [query, setQuery] = useState((queryParam as string) || "")
  const debouncedQuery = useDebounce(query, 300)
  const { location, loading: locationLoading } = useUserLocation()
  const {
    shops,
    loading: shopsLoading,
    hasMore,
    loadMore,
  } = useFilteredShops(debouncedQuery, 10, location, !locationLoading)
  const { ref, inView } = useInView()

  useEffect(() => {
    if (inView && hasMore && !shopsLoading && !locationLoading) {
      loadMore()
    }
  }, [inView, hasMore, shopsLoading, locationLoading, loadMore])

  useEffect(() => {
    if (debouncedQuery !== queryParam) {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, q: debouncedQuery || undefined },
        },
        undefined,
        { shallow: true }
      )
    }
  }, [debouncedQuery, router])

  useEffect(() => {
    if (queryParam && queryParam !== query) {
      setQuery(queryParam as string)
    }
  }, [queryParam])

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
  }

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-bg-green-primary tw-text-white tw-px-4 tw-py-2">
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
          <span className="tw-text-base tw-font-bold">Entdecke deine Lieblings-Betriebe in der getpacked App!</span>
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 tw-w-full sm:tw-w-fit">
            <a
              className="tw-flex tw-w-full sm:tw-w-fit tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 !tw-text-black tw-rounded-lg tw-no-underline tw-transition-transform tw-duration-200 hover:tw-scale-105 tw-bg-white"
              href="https://play.google.com/store/apps/details?id=de.getpacked.app"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GooglePlay size={20} />
              Google Play
            </a>
            <a
              className="tw-flex tw-w-full sm:tw-w-fit tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 !tw-text-black tw-rounded-lg tw-no-underline tw-transition-transform tw-duration-200 hover:tw-scale-105 tw-bg-white"
              href="https://apps.apple.com/de/app/getpacked/id6670163666"
              target="_blank"
              rel="noopener noreferrer"
            >
              <AppStore size={20} />
              App Store
            </a>
          </div>
        </div>
      </div>
      <div className="tw-flex tw-flex-col md:tw-flex-row tw-bg-white">
        <div className="tw-w-full md:tw-w-3/12 tw-h-[50vh] md:tw-h-screen tw-overflow-hidden tw-flex tw-flex-col">
          <div className="tw-sticky tw-top-0 tw-bg-white tw-z-10 tw-p-4">
            <Input type="text" placeholder="Suche nach Betrieben..." value={query} onChange={handleQueryChange} />
          </div>
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-4">
            {locationLoading || (shopsLoading && shops.length === 0) ? (
              <Loader width={20} height={20} />
            ) : (
              <>
                <ul className="tw-mt-4 tw-pl-0">
                  {shops.map(shop => (
                    <ShopCard key={shop.objectID} shop={shop} userLocation={location} />
                  ))}
                </ul>
                {shopsLoading && <Loader width={20} height={20} />}
                {!shopsLoading && hasMore && <div ref={ref} className="tw-h-10" />}
              </>
            )}
          </div>
        </div>
        <div className="tw-w-full md:tw-w-9/12 tw-h-[50vh] md:tw-h-screen">
          {shops.length > 0 && <ShopMap shops={shops} userLocation={location} />}
        </div>
      </div>
    </div>
  )
}

function ShopCard({ shop, userLocation }: { shop: ShopAlgolia; userLocation: { lat: number; lng: number } | null }) {
  const shopLocation = shop._geoloc

  const distance = useMemo(() => {
    if (!userLocation || !shopLocation) return null

    // Fix: Swap coordinates to [lng, lat] for Turf.js
    return getDistance(point([userLocation.lng, userLocation.lat]), point([shopLocation.lon, shopLocation.lat]), {
      units: "kilometers",
    })
  }, [userLocation, shopLocation])

  const image = shop.image ? (typeof shop.image === "string" ? shop.image : shop.image.url) : null

  return (
    <div className="tw-bg-white tw-p-4 tw-flex tw-items-center tw-gap-4 tw-w-full tw-overflow-hidden tw-h-auto md:tw-h-36 tw-border-b">
      {image ? (
        <Image
          src={image}
          alt={shop.name}
          width={60}
          height={60}
          className="tw-rounded-lg tw-object-contain tw-shrink-0"
        />
      ) : (
        <div className="tw-bg-gray-200 tw-w-[60px] tw-h-[60px] tw-rounded-lg tw-flex tw-justify-center tw-items-center tw-shrink-0">
          <Business size={32} />
        </div>
      )}

      <div className="tw-flex-1 tw-overflow-hidden">
        <div>
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-flex-wrap">
            {shop.distributionOptionTypes?.map(type => (
              <div key={type} className="tw-bg-gray-100 tw-inline-block tw-rounded-full tw-px-2 tw-py-1 tw-text-xs">
                {getDistributionOptionLabel(type)}
              </div>
            ))}
          </div>
          <Link href={`/${shop.shorthand}`}>
            <a className="tw-no-underline">
              <h3 className="tw-font-bold tw-text-lg tw-truncate">{shop.name}</h3>
            </a>
          </Link>
        </div>

        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-start md:tw-items-center tw-gap-2">
          {distance && (
            <div className="tw-flex tw-items-center">
              <Location size={18} className="tw-text-gray-500" />
              <span className="tw-text-gray-500">{distance.toFixed(2)} km</span>
            </div>
          )}
          <div className="tw-flex tw-items-center tw-gap-1">
            {shop.paymentOptions.map(option => (
              <PaymentOptionIcon key={option} option={option} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function PaymentOptionIcon({ option }: { option: string }) {
  switch (option) {
    case "cash":
      return <Cash size={16} className="tw-text-gray-500" />
    case "paypal":
      return <Paypal size={16} className="tw-text-gray-500" />
    case "card":
      return <CreditCard size={16} className="tw-text-gray-500" />
    default:
      return null
  }
}

function getDistributionOptionLabel(type: DistributionOption["type"]) {
  switch (type) {
    case "pickup":
      return "Abholung"
    case "delivery":
      return "Lieferung"
    default:
      return type
  }
}
